import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
  Switch,
  InputNumber,
  Divider,
  Spin,
  Table,
  Tag,
  Radio,
  Typography,
  message,
} from 'antd';
import {
  ShopOutlined,
  CloudServerOutlined,
  UserOutlined,
  CreditCardOutlined,
  SettingOutlined,
  AppstoreOutlined,
  DollarCircleOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import RoleAndCategorySelector from '../../components/RoleAndCategorySelector';
import { BANK_OPTIONS, STATUS_OPTIONS, DOCUMENT_STATUS_OPTIONS } from './bankOptions';

const { Text } = Typography;

function ShopEditModal({
  open,
  useritem,
  form,
  loading,
  app_hostname,
  token,
  onCancel,
  onSubmit,
  onUserItemChange,
  onCategoryChange,
  fulfillmentConfig,
  fulfillmentLoading,
  onFulfillmentChange,
}) {
  const [activeTab, setActiveTab] = useState('user-info');

  const [walletData, setWalletData] = useState({ balance: 0, total_earned: 0, total_spent: 0 });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustForm] = Form.useForm();

  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [addressLoading, setAddressLoading] = useState({
    province: false,
    district: false,
    subdistrict: false,
  });

  const fetchWalletData = useCallback(async () => {
    if (!app_hostname || !useritem?.shop_id) return;
    try {
      setWalletLoading(true);
      const formData = new FormData();
      formData.append('buyer_shopid', useritem.shop_id);
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const [balRes, txRes] = await Promise.all([
        axios.post(`${app_hostname}/api/order/wallet/balance`, formData, { headers }),
        axios.post(`${app_hostname}/api/order/wallet/transactions`, formData, { headers }),
      ]);

      if (balRes.data?.status) {
        setWalletData(balRes.data);
      }
      if (txRes.data?.status && Array.isArray(txRes.data.transactions)) {
        setWalletTransactions(txRes.data.transactions);
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
    } finally {
      setWalletLoading(false);
    }
  }, [app_hostname, useritem?.shop_id]);

  useEffect(() => {
    if (open && activeTab === 'coins-wallet') {
      fetchWalletData();
    }
  }, [open, activeTab, fetchWalletData]);

  const handleAdjustSubmit = async (values) => {
    if (!app_hostname || !useritem?.shop_id) return;
    try {
      setAdjustLoading(true);
      const formData = new FormData();
      formData.append('buyer_shopid', useritem.shop_id);
      formData.append('action', values.action);
      formData.append('amount', String(values.amount));
      formData.append('description', values.description || '');
      const adminUser = localStorage.getItem('username') || 'Admin';
      formData.append('admin_username', adminUser);

      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const res = await axios.post(`${app_hostname}/api/order/wallet/admin/adjust`, formData, {
        headers,
      });
      if (res.data?.status) {
        message.success('ปรับปรุงยอดเหรียญสำเร็จ');
        setIsAdjustModalOpen(false);
        adjustForm.resetFields();
        fetchWalletData();
      } else {
        message.error(res.data?.message || 'ปรับปรุงยอดเหรียญไม่สำเร็จ');
      }
    } catch (err) {
      message.error(
        err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการปรับปรุงยอดเหรียญ',
      );
    } finally {
      setAdjustLoading(false);
    }
  };

  const fetchProvinces = useCallback(async () => {
    if (!app_hostname) return;
    try {
      setAddressLoading((prev) => ({ ...prev, province: true }));
      const formData = new FormData();
      formData.append('province', 'all');
      const res = await axios.post(
        `${app_hostname}/api/account/register/address/getprovince`,
        formData,
        {
          headers: {
            Accept: 'application/json',
            Authorization: 'Basic ' + btoa('Hvqhzd3f_zAXSsK7x2QfslGn0EvBoj'),
          },
        },
      );
      if (res.data?.status === 'OK' && Array.isArray(res.data.data_province)) {
        setProvinces(res.data.data_province);
      }
    } catch (err) {
      console.error('Error fetching provinces via API:', err);
    } finally {
      setAddressLoading((prev) => ({ ...prev, province: false }));
    }
  }, [app_hostname]);

  const fetchDistricts = useCallback(
    async (provinceName) => {
      if (!app_hostname || !provinceName) {
        setDistricts([]);
        setSubdistricts([]);
        return;
      }
      try {
        setAddressLoading((prev) => ({ ...prev, district: true }));
        const formData = new FormData();
        formData.append('province', provinceName);
        const res = await axios.post(
          `${app_hostname}/api/account/register/address/getamphure`,
          formData,
          {
            headers: {
              Accept: 'application/json',
              Authorization: 'Basic ' + btoa('Hvqhzd3f_zAXSsK7x2QfslGn0EvBoj'),
            },
          },
        );
        if (res.data?.status === 'OK' && Array.isArray(res.data.data_amphure)) {
          setDistricts(res.data.data_amphure);
        }
      } catch (err) {
        console.error('Error fetching districts via API:', err);
      } finally {
        setAddressLoading((prev) => ({ ...prev, district: false }));
      }
    },
    [app_hostname],
  );

  const fetchSubdistricts = useCallback(
    async (provinceName, districtName) => {
      if (!app_hostname || !provinceName || !districtName) {
        setSubdistricts([]);
        return;
      }
      try {
        setAddressLoading((prev) => ({ ...prev, subdistrict: true }));
        const formData = new FormData();
        formData.append('province', provinceName);
        formData.append('amphur', districtName);
        const res = await axios.post(
          `${app_hostname}/api/account/register/address/gettambol`,
          formData,
          {
            headers: {
              Accept: 'application/json',
              Authorization: 'Basic ' + btoa('Hvqhzd3f_zAXSsK7x2QfslGn0EvBoj'),
            },
          },
        );
        if (res.data?.status === 'OK' && Array.isArray(res.data.data_tambol)) {
          setSubdistricts(res.data.data_tambol);
        }
      } catch (err) {
        console.error('Error fetching subdistricts via API:', err);
      } finally {
        setAddressLoading((prev) => ({ ...prev, subdistrict: false }));
      }
    },
    [app_hostname],
  );

  useEffect(() => {
    if (open) {
      fetchProvinces();
      if (useritem?.shop_province) {
        fetchDistricts(useritem.shop_province);
        if (useritem?.shop_district) {
          fetchSubdistricts(useritem.shop_province, useritem.shop_district);
        }
      }
    }
  }, [
    open,
    useritem?.shop_province,
    useritem?.shop_district,
    fetchProvinces,
    fetchDistricts,
    fetchSubdistricts,
  ]);

  const provinceOptions = useMemo(() => {
    const list = provinces.map((p) => ({ label: p, value: p }));
    if (useritem?.shop_province && !list.some((item) => item.value === useritem.shop_province)) {
      return [{ label: useritem.shop_province, value: useritem.shop_province }, ...list];
    }
    return list;
  }, [provinces, useritem?.shop_province]);

  const districtOptions = useMemo(() => {
    const list = districts.map((d) => ({ label: d, value: d }));
    if (useritem?.shop_district && !list.some((item) => item.value === useritem.shop_district)) {
      return [{ label: useritem.shop_district, value: useritem.shop_district }, ...list];
    }
    return list;
  }, [districts, useritem?.shop_district]);

  const subdistrictOptions = useMemo(() => {
    const list = subdistricts.map((s) => ({
      label: s.name_th,
      value: s.name_th,
      zip_code: s.zip_code ? String(s.zip_code) : '',
    }));
    if (
      useritem?.shop_subdistrict &&
      !list.some((item) => item.value === useritem.shop_subdistrict)
    ) {
      return [
        {
          label: useritem.shop_subdistrict,
          value: useritem.shop_subdistrict,
          zip_code: useritem.shop_postcode || '',
        },
        ...list,
      ];
    }
    return list;
  }, [subdistricts, useritem?.shop_subdistrict, useritem?.shop_postcode]);

  return (
    <Modal
      title={
        <Space>
          <ShopOutlined />
          รหัสร้านค้า: {useritem.shop_id}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1150}
      centered
      className='custom-modal'
      destroyOnClose
    >
      <Form form={form} layout='vertical' onFinish={onSubmit} style={{ marginTop: '16px' }}>
        <div
          className='shop-modal-layout'
          style={{ display: 'flex', minHeight: '440px', gap: '24px' }}
        >
          {/* Left Navigation Menu */}
          <div
            className='shop-modal-sidebar'
            style={{
              width: '220px',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              borderRight: '1px solid #f1f5f9',
              paddingRight: '16px',
            }}
          >
            <div
              className={`shop-sidebar-tab ${activeTab === 'user-info' ? 'active' : ''}`}
              onClick={() => setActiveTab('user-info')}
            >
              <Space>
                <UserOutlined />
                ข้อมูลผู้ใช้
              </Space>
            </div>
            <div
              className={`shop-sidebar-tab ${activeTab === 'bank' ? 'active' : ''}`}
              onClick={() => setActiveTab('bank')}
            >
              <Space>
                <CreditCardOutlined />
                บัญชีธนาคาร
              </Space>
            </div>
            <div
              className={`shop-sidebar-tab ${activeTab === 'account-settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('account-settings')}
            >
              <Space>
                <SettingOutlined />
                ตั้งค่าบัญชี
              </Space>
            </div>
            <div
              className={`shop-sidebar-tab ${activeTab === 'role-permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('role-permissions')}
            >
              <Space>
                <AppstoreOutlined />
                สิทธิ์เข้าถึงสินค้า
              </Space>
            </div>
            <div
              className={`shop-sidebar-tab ${activeTab === 'fulfillment' ? 'active' : ''}`}
              onClick={() => setActiveTab('fulfillment')}
            >
              <Space>
                <CloudServerOutlined />
                ตั้งค่า Fulfillment
              </Space>
            </div>
            <div
              className={`shop-sidebar-tab ${activeTab === 'coins-wallet' ? 'active' : ''}`}
              onClick={() => setActiveTab('coins-wallet')}
            >
              <Space>
                <DollarCircleOutlined style={{ color: '#d97706' }} />
                Meya Coins
              </Space>
            </div>
          </div>

          {/* Right Content Area */}
          <div
            className='shop-modal-content-area'
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '8px',
              maxHeight: '520px',
            }}
          >
            {activeTab === 'user-info' && (
              <div className='tab-pane-content'>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_firstname' label='ชื่อจริง'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_firstname: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_lastname' label='นามสกุล'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_lastname: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_email' label='อีเมล'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_email: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_username' label='ชื่อผู้ใช้'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_username: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_name' label='ชื่อร้านค้า'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_name: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_address' label='ที่อยู่'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_address: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_province' label='จังหวัด'>
                      <Select
                        showSearch
                        allowClear
                        placeholder='เลือกจังหวัด'
                        loading={addressLoading.province}
                        options={provinceOptions}
                        optionFilterProp='label'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(val) => {
                          const newProvince = val || '';
                          form.setFieldsValue({
                            shop_province: newProvince,
                            shop_district: undefined,
                            shop_subdistrict: undefined,
                            shop_postcode: undefined,
                          });
                          onUserItemChange({
                            shop_province: newProvince,
                            shop_district: '',
                            shop_subdistrict: '',
                            shop_postcode: '',
                          });
                          fetchDistricts(newProvince);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_district' label='อำเภอ / เขต'>
                      <Select
                        showSearch
                        allowClear
                        placeholder='เลือกอำเภอ / เขต'
                        loading={addressLoading.district}
                        options={districtOptions}
                        disabled={!form.getFieldValue('shop_province') && !useritem.shop_province}
                        optionFilterProp='label'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(val) => {
                          const newDistrict = val || '';
                          const currentProvince =
                            form.getFieldValue('shop_province') || useritem.shop_province || '';
                          form.setFieldsValue({
                            shop_district: newDistrict,
                            shop_subdistrict: undefined,
                            shop_postcode: undefined,
                          });
                          onUserItemChange({
                            shop_district: newDistrict,
                            shop_subdistrict: '',
                            shop_postcode: '',
                          });
                          fetchSubdistricts(currentProvince, newDistrict);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_subdistrict' label='ตำบล / แขวง'>
                      <Select
                        showSearch
                        allowClear
                        placeholder='เลือกตำบล / แขวง'
                        loading={addressLoading.subdistrict}
                        options={subdistrictOptions}
                        disabled={
                          (!form.getFieldValue('shop_province') && !useritem.shop_province) ||
                          (!form.getFieldValue('shop_district') && !useritem.shop_district)
                        }
                        optionFilterProp='label'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(val, option) => {
                          const newSubdistrict = val || '';
                          const autoZip = option?.zip_code || '';

                          form.setFieldsValue({
                            shop_subdistrict: newSubdistrict,
                            ...(autoZip ? { shop_postcode: autoZip } : {}),
                          });
                          onUserItemChange({
                            shop_subdistrict: newSubdistrict,
                            ...(autoZip ? { shop_postcode: autoZip } : {}),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_postcode' label='รหัสไปรษณีย์'>
                      <Input
                        placeholder='รหัสไปรษณีย์'
                        onChange={(e) => {
                          onUserItemChange({ shop_postcode: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='shop_telno' label='เบอร์โทรศัพท์'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ shop_telno: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === 'bank' && (
              <div className='tab-pane-content'>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='bank_name' label='ชื่อธนาคาร'>
                      <Select
                        showSearch
                        placeholder='เลือกหรือค้นหาธนาคาร'
                        options={BANK_OPTIONS}
                        optionFilterProp='label'
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => {
                          onUserItemChange({ bank_name: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='bank_acc_id' label='เลขบัญชีธนาคาร'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ bank_acc_id: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='bank_acc_name' label='ชื่อบัญชีธนาคาร'>
                      <Input
                        onChange={(e) => {
                          onUserItemChange({ bank_acc_name: e.target.value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === 'account-settings' && (
              <div className='tab-pane-content'>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name='status' label='สถานะบัญชี'>
                      <Select
                        options={STATUS_OPTIONS}
                        onChange={(value) => {
                          onUserItemChange({ status: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name='shopdocument' label='สถานะเอกสาร'>
                      <Select
                        options={DOCUMENT_STATUS_OPTIONS}
                        onChange={(value) => {
                          onUserItemChange({ shopdocument: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === 'role-permissions' && (
              <div className='tab-pane-content'>
                <RoleAndCategorySelector
                  userItem={useritem}
                  appHostname={app_hostname}
                  token={token}
                  setUserItem={(updater) => {
                    if (typeof updater === 'function') {
                      onUserItemChange(updater(useritem));
                    } else {
                      onUserItemChange(updater);
                    }
                  }}
                  onCategoryChange={onCategoryChange}
                />
              </div>
            )}

            {activeTab === 'fulfillment' && (
              <div className='tab-pane-content'>
                <Spin spinning={fulfillmentLoading}>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item label='สถานะ Fulfillment'>
                        <Switch
                          checked={fulfillmentConfig?.is_active || false}
                          checkedChildren='เปิดใช้งาน'
                          unCheckedChildren='ปิดใช้งาน'
                          onChange={(checked) =>
                            onFulfillmentChange({ ...fulfillmentConfig, is_active: checked })
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label='ค่ายจัดส่ง (Provider)'>
                        <Select
                          value={fulfillmentConfig?.provider_type || ''}
                          options={[
                            { label: 'ไม่ระบุ', value: '' },
                            { label: 'PACKHAI', value: 'PACKHAI' },
                          ]}
                          onChange={(value) =>
                            onFulfillmentChange({ ...fulfillmentConfig, provider_type: value })
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {fulfillmentConfig?.provider_type === 'PACKHAI' && (
                    <>
                      <Divider orientation='left' plain style={{ fontSize: 13, marginTop: 12 }}>
                        การตั้งค่า PACKHAI
                      </Divider>
                      <Row gutter={16}>
                        <Col xs={24}>
                          <Form.Item
                            label='API Token'
                            validateStatus={
                              fulfillmentConfig?.is_active &&
                              fulfillmentConfig?.provider_type === 'PACKHAI' &&
                              !fulfillmentConfig?.config_json?.api_token
                                ? 'error'
                                : ''
                            }
                            help={
                              fulfillmentConfig?.is_active &&
                              fulfillmentConfig?.provider_type === 'PACKHAI' &&
                              !fulfillmentConfig?.config_json?.api_token
                                ? 'กรุณากรอก API Token'
                                : ''
                            }
                          >
                            <Input.Password
                              visibilityToggle
                              value={fulfillmentConfig?.config_json?.api_token || ''}
                              onChange={(e) =>
                                onFulfillmentChange({
                                  ...fulfillmentConfig,
                                  config_json: {
                                    ...fulfillmentConfig.config_json,
                                    api_token: e.target.value,
                                  },
                                })
                              }
                              placeholder='กรอก API Token จาก Packhai'
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item label='Warehouse ID'>
                            <InputNumber
                              style={{ width: '100%' }}
                              value={fulfillmentConfig?.config_json?.warehouse_id || 0}
                              onChange={(val) =>
                                onFulfillmentChange({
                                  ...fulfillmentConfig,
                                  config_json: {
                                    ...fulfillmentConfig.config_json,
                                    warehouse_id: val,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item label='Express ID'>
                            <InputNumber
                              style={{ width: '100%' }}
                              value={fulfillmentConfig?.config_json?.express_id || 0}
                              onChange={(val) =>
                                onFulfillmentChange({
                                  ...fulfillmentConfig,
                                  config_json: {
                                    ...fulfillmentConfig.config_json,
                                    express_id: val,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item label='Sync Interval'>
                            <Select
                              value={fulfillmentConfig?.config_json?.sync_interval || '1h'}
                              options={[
                                { label: '30 นาที', value: '30m' },
                                { label: '1 ชั่วโมง', value: '1h' },
                                { label: '2 ชั่วโมง', value: '2h' },
                                { label: '4 ชั่วโมง', value: '4h' },
                                { label: '6 ชั่วโมง', value: '6h' },
                                { label: '12 ชั่วโมง', value: '12h' },
                                { label: '24 ชั่วโมง (1 วัน)', value: '24h' },
                              ]}
                              onChange={(value) =>
                                onFulfillmentChange({
                                  ...fulfillmentConfig,
                                  config_json: {
                                    ...fulfillmentConfig.config_json,
                                    sync_interval: value,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </Spin>
              </div>
            )}

            {activeTab === 'coins-wallet' && (
              <div className='tab-pane-content font-th'>
                <Spin spinning={walletLoading}>
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #fffbeb 0%, #ffffff 100%)',
                      border: '1px solid #fef3c7',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px',
                    }}
                  >
                    <Row gutter={[16, 16]} align='middle' justify='space-between'>
                      <Col xs={24} sm={14}>
                        <Space direction='vertical' size={4}>
                          <Text type='secondary' style={{ fontSize: '13px' }}>
                            ยอดเหรียญ Meya Coins คงเหลือ
                          </Text>
                          <div style={{ fontSize: '28px', fontWeight: 800, color: '#d97706' }}>
                            ฿
                            {Number(walletData.balance || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <Space size={16} style={{ marginTop: '4px' }}>
                            <span style={{ fontSize: '12px', color: '#16a34a' }}>
                              ได้รับสะสม: +฿
                              {Number(walletData.total_earned || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                              ใช้ไปแล้ว: ฿
                              {Number(walletData.total_spent || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </Space>
                        </Space>
                      </Col>
                      <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
                        <Button
                          type='primary'
                          icon={<DollarCircleOutlined />}
                          style={{
                            background: '#f59e0b',
                            borderColor: '#f59e0b',
                            borderRadius: '8px',
                            height: '38px',
                            fontWeight: 600,
                          }}
                          onClick={() => {
                            adjustForm.setFieldsValue({
                              action: 'credit',
                              amount: 10,
                              description: '',
                            });
                            setIsAdjustModalOpen(true);
                          }}
                        >
                          ปรับปรุงยอดเหรียญ
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  <Divider
                    orientation='left'
                    plain
                    style={{ fontSize: 13, margin: '16px 0 12px 0' }}
                  >
                    ประวัติธุรกรรมเหรียญ (Ledger Statement)
                  </Divider>

                  <Table
                    size='small'
                    dataSource={walletTransactions}
                    rowKey='transaction_id'
                    pagination={{ pageSize: 5 }}
                    locale={{ emptyText: 'ยังไม่มีประวัติการทำรายการเหรียญ' }}
                    columns={[
                      {
                        title: 'วันเวลา',
                        dataIndex: 'createtime',
                        key: 'createtime',
                        width: 140,
                        render: (text) => (text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-'),
                      },
                      {
                        title: 'ประเภท',
                        dataIndex: 'transaction_type',
                        key: 'transaction_type',
                        width: 140,
                        render: (type) => {
                          let color = 'blue';
                          let label = 'ปรับปรุง';
                          if (type === 'CASHBACK_EARN') {
                            color = 'green';
                            label = 'รับเงินคืน';
                          } else if (type === 'ORDER_DISCOUNT') {
                            color = 'orange';
                            label = 'ใช้ส่วนลด';
                          } else if (type === 'ORDER_REFUND') {
                            color = 'purple';
                            label = 'คืนเหรียญ';
                          } else if (type === 'MANUAL_ADJUST') {
                            color = 'cyan';
                            label = 'Admin ปรับยอด';
                          }
                          return <Tag color={color}>{label}</Tag>;
                        },
                      },
                      {
                        title: 'จำนวน',
                        dataIndex: 'amount',
                        key: 'amount',
                        width: 100,
                        align: 'right',
                        render: (amt, row) => {
                          const isPlus =
                            row.transaction_type === 'CASHBACK_EARN' ||
                            row.transaction_type === 'ORDER_REFUND' ||
                            (row.transaction_type === 'MANUAL_ADJUST' && amt > 0);
                          return (
                            <Text strong style={{ color: isPlus ? '#16a34a' : '#ea580c' }}>
                              {isPlus ? '+' : '-'}฿
                              {Math.abs(amt).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </Text>
                          );
                        },
                      },
                      {
                        title: 'คงเหลือ',
                        dataIndex: 'balance_after',
                        key: 'balance_after',
                        width: 100,
                        align: 'right',
                        render: (bal) =>
                          `฿${Number(bal).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                      },
                      {
                        title: 'เลขอ้างอิง / รายละเอียด',
                        key: 'desc',
                        render: (_, row) => (
                          <Space direction='vertical' size={0}>
                            {row.order_id && (
                              <Text strong style={{ fontSize: '12px' }}>
                                ออเดอร์: {row.order_id}
                              </Text>
                            )}
                            <Text type='secondary' style={{ fontSize: '11px' }}>
                              {row.description || '-'}
                            </Text>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Spin>
              </div>
            )}
          </div>
        </div>

        {/* Right-aligned Footer */}
        <div
          className='shop-edit-modal-footer'
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px',
            borderTop: '1px solid #f1f5f9',
            paddingTop: '16px',
            flexDirection: 'row',
          }}
        >
          <Button
            className='btn-soft-red btn-footer-modal'
            onClick={onCancel}
            style={{ height: '42px', borderRadius: '8px' }}
          >
            ยกเลิก
          </Button>
          <Button
            className='btn-soft-blue btn-footer-modal'
            type='primary'
            htmlType='submit'
            loading={loading}
            style={{ height: '42px', borderRadius: '8px' }}
          >
            บันทึก
          </Button>
        </div>
      </Form>

      {/* Adjust Coins Modal */}
      <Modal
        title={
          <Space>
            <DollarCircleOutlined style={{ color: '#d97706' }} />
            <span>ปรับปรุงยอด Meya Coins ({useritem?.shop_id})</span>
          </Space>
        }
        open={isAdjustModalOpen}
        onCancel={() => setIsAdjustModalOpen(false)}
        footer={null}
        destroyOnClose
        width={480}
      >
        <Form form={adjustForm} layout='vertical' onFinish={handleAdjustSubmit}>
          <Form.Item
            name='action'
            label='การดำเนินการ'
            rules={[{ required: true, message: 'กรุณาเลือกการดำเนินการ' }]}
          >
            <Radio.Group buttonStyle='solid'>
              <Radio.Button value='credit'>
                <Space>
                  <PlusOutlined style={{ color: '#16a34a' }} />
                  เพิ่มเหรียญ (Credit)
                </Space>
              </Radio.Button>
              <Radio.Button value='debit'>
                <Space>
                  <MinusOutlined style={{ color: '#ea580c' }} />
                  หักเหรียญ (Debit)
                </Space>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name='amount'
            label='จำนวนเหรียญ (บาท)'
            rules={[
              { required: true, message: 'กรุณากรอกจำนวนเหรียญ' },
              { type: 'number', min: 0.01, message: 'จำนวนเหรียญต้องมากกว่า 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder='เช่น 50'
              min={0.01}
              precision={2}
              prefix='฿'
            />
          </Form.Item>

          <Form.Item
            name='description'
            label='เหตุผล / บันทึกช่วยจำ'
            rules={[{ required: true, message: 'กรุณาระบุเหตุผลการปรับปรุงยอด' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder='เช่น ชดเชยกรณีจัดส่งล่าช้า, แก้ไขข้อพิพาทออเดอร์...'
            />
          </Form.Item>

          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}
          >
            <Button onClick={() => setIsAdjustModalOpen(false)}>ยกเลิก</Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={adjustLoading}
              style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
            >
              ยืนยันปรับปรุงยอด
            </Button>
          </div>
        </Form>
      </Modal>
    </Modal>
  );
}

ShopEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  useritem: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  app_hostname: PropTypes.string,
  token: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUserItemChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  fulfillmentConfig: PropTypes.object,
  fulfillmentLoading: PropTypes.bool,
  onFulfillmentChange: PropTypes.func.isRequired,
};

ShopEditModal.defaultProps = {
  loading: false,
  token: '',
};

export default ShopEditModal;
